/**
 * UserContext — данные текущего пользователя из `/api/user/userinfo` (BE-9).
 *
 * Запрос делается **только** после `keycloak.initialized + authenticated`.
 * До этого `user === null` (и компоненты должны это уметь).
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { useKeycloak } from '../keycloak/KeycloakProvider';
import { api } from '../requests/api';
import type { UserInfo } from '../types/user';

interface UserContextValue {
  user: UserInfo | null;
  loading: boolean;
  error: Error | null;
}

export const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { initialized, keycloak } = useKeycloak();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!initialized || !keycloak.authenticated) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<UserInfo>('/user/userinfo')
      .then((response) => {
        if (!cancelled) {
          setUser(response.data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialized, keycloak.authenticated]);

  return (
    <UserContext.Provider value={{ user, loading, error }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used inside <UserProvider>');
  }
  return ctx;
};
